// Solo dashboard /me (§5.2), printed as one ticket to match the blend page:
// an ink hero with your critic type, a ledger that counts up, and perforated
// fields for your rating personality, eras, logging, invite link and blends.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Page, Skeleton } from "../components/Layout.tsx";
import {
  Sheet,
  Masthead,
  Tear,
  Field,
  Kicker,
  H3,
  Sub,
  Empty,
  Barcode,
  Count,
} from "../components/sheet.tsx";
import {
  RatingHistogram,
  DecadeBars,
  HeatStrip,
  ScoreDial,
} from "../components/Charts.tsx";
import { PosterCard } from "../components/brand.tsx";
import { Dropzone } from "../components/Dropzone.tsx";
import { FeelingLucky } from "../components/FeelingLucky.tsx";
import { TasteBadges } from "../components/TasteBadges.tsx";
import { computeTasteSignals, topLoved } from "../lib/tasteSignals.ts";
import { usePosters } from "../lib/usePosters.ts";
import { useCountUpProgress } from "../lib/useCountUp.ts";
import { useAuth } from "../auth/AuthProvider.tsx";
import { friendlyAuthError } from "../lib/anonMode.ts";
import { track } from "../lib/analytics.ts";
import {
  getMyProfile,
  listMyBlends,
  updateProfile,
  deleteProfile,
  generateProfileSummary,
  generateProfileBadges,
  setPublicHidden,
  setSyncDisabled,
  syncProfile,
  type Profile,
  type BlendSummary,
  type TasteBadge,
} from "../lib/api.ts";

export default function Dashboard() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [blends, setBlends] = useState<BlendSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUpdate, setShowUpdate] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [badges, setBadges] = useState<TasteBadge[] | null>(null);
  const [badgesLoading, setBadgesLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(
    params.get("toast") === "self-invite"
      ? "That's your own link — send it to a friend."
      : null,
  );

  useEffect(() => {
    if (auth.loading) return;
    if (!auth.user) {
      navigate("/", { replace: true });
      return;
    }
    (async () => {
      const p = await getMyProfile().catch(() => null);
      if (!p) {
        navigate("/onboarding", { replace: true });
        return;
      }
      setProfile(p);
      setSummary(p.summary ?? null);
      setBadges(p.badges ?? null);
      setLoading(false);
      listMyBlends()
        .then(setBlends)
        .catch(() => {});

      // Lazy drift-sync from their public Letterboxd diary, after paint. Self-
      // throttled server-side, so visiting the dashboard often is free.
      if (p.letterboxd_username) {
        syncProfile(p.letterboxd_username).then((r) => {
          if (!r?.changed || !r.added) return;
          getMyProfile()
            .then((fresh) => {
              if (fresh) setProfile(fresh);
            })
            .catch(() => {});
        });
      }
    })();
  }, [auth.loading, auth.user, navigate]);

  // Generate the one-line taste blurb in the background when it's missing.
  useEffect(() => {
    if (!profile || summary) return;
    let cancelled = false;
    setSummaryLoading(true);
    generateProfileSummary()
      .then((r) => {
        if (!cancelled && r.summary) setSummary(r.summary);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setSummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [profile, summary]);

  // Mint the personalized badge stickers in the background when missing.
  useEffect(() => {
    if (!profile || (badges && badges.length)) return;
    let cancelled = false;
    setBadgesLoading(true);
    generateProfileBadges()
      .then((r) => {
        if (!cancelled && r.badges?.length) setBadges(r.badges);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setBadgesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [profile, badges]);

  async function regenerateBadges() {
    setBadgesLoading(true);
    try {
      const r = await generateProfileBadges(true);
      if (r.badges?.length) setBadges(r.badges);
    } catch {
      /* keep old set */
    } finally {
      setBadgesLoading(false);
    }
  }

  async function regenerateSummary() {
    setSummaryLoading(true);
    try {
      const r = await generateProfileSummary(true);
      if (r.summary) setSummary(r.summary);
    } catch {
      /* keep old */
    } finally {
      setSummaryLoading(false);
    }
  }

  if (loading || !profile) {
    return (
      <Page testid="screen-dashboard" wide bare>
        <div className="desk">
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
      </Page>
    );
  }

  return (
    <Page testid="screen-dashboard" wide bare>
      {toast && (
        <div
          data-testid="toast"
          className="mx-auto mb-3 max-w-app rounded-lg border border-green-500/40 bg-card p-3 text-sm"
        >
          {toast}
          <button
            className="ml-2 text-ink-400 underline"
            onClick={() => setToast(null)}
          >
            dismiss
          </button>
        </div>
      )}
      {auth.isAnonymous && <ClaimProfileBanner />}
      <SoloSheet
        profile={profile}
        blends={blends}
        summary={summary}
        summaryLoading={summaryLoading}
        onRegenerateSummary={regenerateSummary}
        badges={badges}
        badgesLoading={badgesLoading}
        onRegenerateBadges={regenerateBadges}
        showUpdate={showUpdate}
        setShowUpdate={setShowUpdate}
        onUpdated={setProfile}
        onSignOut={() => auth.signOut().then(() => navigate("/"))}
        onDelete={async () => {
          if (
            confirm("Delete your account and all blends? This can't be undone.")
          ) {
            await deleteProfile();
            await auth.signOut();
            navigate("/");
          }
        }}
      />
    </Page>
  );
}

/** Guest sessions live in one browser's localStorage: clear it (or switch
 *  devices) and the profile + its blends are orphaned. Claiming attaches an
 *  OAuth identity to the SAME user_id, so everything survives. */
function ClaimProfileBanner() {
  const auth = useAuth();
  const [error, setError] = useState<string | null>(null);
  const claim = (provider: "discord" | "spotify") => {
    track("claim_profile_start", { provider });
    setError(null);
    auth.linkIdentity(provider).catch((e) => {
      track("claim_profile_error", {
        provider,
        error: String((e as Error)?.message ?? e),
      });
      setError(friendlyAuthError(e));
    });
  };
  return (
    <div
      data-testid="claim-banner"
      className="mx-auto mb-3 flex max-w-app flex-wrap items-center gap-3 rounded-lg border border-gold-500/50 bg-card p-3 text-sm"
    >
      <span className="text-ink-500">
        <strong className="text-ink-900">Guest profile</strong> — it lives only
        in this browser. Claim it to keep your blends if you clear data or
        switch devices.
      </span>
      <span className="flex gap-2">
        <button
          type="button"
          data-testid="action-claim-discord"
          className="btn-ghost"
          onClick={() => claim("discord")}
        >
          Claim with Discord
        </button>
        <button
          type="button"
          data-testid="action-claim-spotify"
          className="btn-ghost"
          onClick={() => claim("spotify")}
        >
          Spotify
        </button>
      </span>
      {error && <span className="w-full text-red-600">{error}</span>}
    </div>
  );
}

function SoloSheet({
  profile,
  blends,
  summary,
  summaryLoading,
  onRegenerateSummary,
  badges,
  badgesLoading,
  onRegenerateBadges,
  showUpdate,
  setShowUpdate,
  onUpdated,
  onSignOut,
  onDelete,
}: {
  profile: Profile;
  blends: BlendSummary[];
  summary: string | null;
  summaryLoading: boolean;
  onRegenerateSummary: () => void;
  badges: TasteBadge[] | null;
  badgesLoading: boolean;
  onRegenerateBadges: () => void;
  showUpdate: boolean;
  setShowUpdate: (f: (s: boolean) => boolean) => void;
  onUpdated: (p: Profile) => void;
  onSignOut: () => void;
  onDelete: () => void;
}) {
  const data = profile.profile_data;
  const name = profile.display_name;
  const s = useMemo(() => computeTasteSignals(data), [data]);
  const prog = useCountUpProgress();
  const cu = (n: number) => Math.round(n * prog);
  const dial = Math.round((s.avgRating / 5) * 100);

  // Loved films drifting behind the headline.
  const loved = useMemo(() => topLoved(data, 14), [data]);
  const posters = usePosters(loved);

  return (
    <Sheet testid="solo-sheet">
      <Masthead right="Admit one" sub={`Now showing · ${name}`} />

      {/* Ink hero — your critic type, with a filmstrip of what you love behind it */}
      <section
        data-testid="taste-signature"
        className="heroband bg-ink-900 text-paper-50"
      >
        {loved.length > 0 && (
          <div className="marq" aria-hidden>
            <div className="marqin">
              {[0, 1].map((dup) => (
                <div className="mset" key={dup}>
                  {loved.map((f) => (
                    <div className="mp" key={`${dup}-${f.uri}`}>
                      <PosterCard
                        title={f.title}
                        year={f.year}
                        poster={posters[f.uri]}
                        width="100%"
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="marqveil" aria-hidden />
        <div className="relative z-[2] flex flex-col items-center gap-4 px-6 py-9 text-center sm:px-10">
          <div style={{ color: "var(--paper-50)" }}>
            <ScoreDial
              score={dial}
              size={104}
              label={`★${s.avgRating.toFixed(1)}`}
            />
          </div>
          <div>
            <p className="font-mono text-[11px] uppercase tracking-stamp text-ink-300">
              {name}'s rating personality
            </p>
            <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tightish sm:text-4xl">
              {s.criticType} <span className="text-gold-500">critic</span>
            </h1>
            <p className="mx-auto mt-2 max-w-md text-sm text-paper-200/85">
              {s.criticBlurb}
            </p>
            <p
              className="mono-meta mx-auto mt-2 text-ink-300"
              data-testid="score-explainer"
            >
              ★{s.avgRating.toFixed(2)} / 5 average
            </p>
          </div>
        </div>
      </section>

      {/* The ledger */}
      <section className="px-6 py-6 sm:px-10">
        <div className="flex flex-wrap gap-x-10 gap-y-4">
          <Count n={cu(data.counts.watched)} label="Watched" />
          <Count n={cu(data.counts.rated)} label="Rated" tone="red" />
          <Count
            n={`★${s.avgRating.toFixed(2)}` as unknown as number}
            label="Average"
            tone="gold"
          />
          <Count
            n={cu(data.counts.watchlist)}
            label="Watchlist"
            tone={data.counts.watchlist ? "clay" : "muted"}
          />
        </div>
      </section>

      <Tear />

      {/* Your taste in one line — GLM-written, lazy-loaded */}
      {(summary || summaryLoading) && (
        <>
          <Field testid="section-taste-summary">
            <Kicker>In one line</Kicker>
            {summary && !summaryLoading ? (
              <>
                <p
                  key={summary}
                  data-testid="taste-summary"
                  className="reprint mt-2 font-display text-xl font-medium italic leading-snug text-ink-900 sm:text-2xl"
                >
                  “{summary}”
                </p>
                <button
                  type="button"
                  data-testid="action-regenerate-taste"
                  onClick={onRegenerateSummary}
                  className="mt-3 font-mono text-[11px] font-bold uppercase tracking-stamp text-ink-400 underline underline-offset-2 hover:text-red-600"
                >
                  Don't like it? Get another ↻
                </button>
              </>
            ) : (
              <div className="mt-3" data-testid="taste-summary-loading">
                <div className="h-5 w-3/4 rounded shimmer" />
                <div className="mt-2 h-5 w-1/2 rounded shimmer" />
                <p className="mono-meta mt-2 text-ink-400">
                  Reading your taste…
                </p>
              </div>
            )}
          </Field>
          <Tear />
        </>
      )}

      {/* Invite link — front and center: it's the whole growth loop */}
      <Field testid="section-invite">
        <Kicker>Two movie libraries, one screening</Kicker>
        <H3>Your link</H3>
        <Sub>
          Send this to anyone whose taste you're curious about. When they join,
          you both get a blend.
        </Sub>
        <InviteRow shareSlug={profile.share_slug} />
        {(data.letterboxdUsername ?? profile.letterboxd_username) && (
          <PublicPageRow
            username={(data.letterboxdUsername ?? profile.letterboxd_username)!}
            initialHidden={!!profile.public_hidden}
            initialSyncDisabled={!!profile.sync_disabled}
            syncedCount={profile.synced_count ?? 0}
          />
        )}
      </Field>

      <Tear />

      {/* I'm feeling lucky — a GLM pick you haven't seen */}
      <FeelingLucky />

      <Tear />

      {/* Your taste archetypes — a collectible "type" */}
      <TasteBadges
        profile={data}
        badges={badges}
        loading={badgesLoading && !(badges && badges.length)}
        onRegenerate={onRegenerateBadges}
      />

      <Tear />

      {/* Who you are as a viewer — the gauges */}
      <Field>
        <Kicker>Your signature</Kicker>
        <H3>What defines your taste</H3>
        <div className="mt-4 grid grid-cols-2 gap-x-8 gap-y-5 sm:grid-cols-4">
          <Gauge
            value={s.definingDecade ?? "—"}
            label="Home decade"
            sub={
              s.definingDecade
                ? `${Math.round(s.definingDecadeShare * 100)}% of watches`
                : "no data"
            }
            tone="red"
          />
          <Gauge
            value={s.erasSpanned ? `${s.erasSpanned}` : "—"}
            label="Eras spanned"
            sub="decades of film"
            tone="ink"
          />
          <Gauge
            value={s.backlog.toLocaleString()}
            label="Backlog"
            sub="on the watchlist"
            tone="gold"
          />
          <Gauge
            value={`${Math.round(s.ratedShare * 100)}%`}
            label="Rated"
            sub="of your library"
            tone="clay"
          />
        </div>
      </Field>

      <Tear />

      <Field>
        <Kicker>How you score</Kicker>
        <H3>Rating personality</H3>
        <div className="mt-4">
          <RatingHistogram hist={data.aggregates.ratingHist} />
        </div>
      </Field>

      <Tear />

      <Field>
        <Kicker>Across the century</Kicker>
        <H3>By decade</H3>
        <div className="mt-4">
          <DecadeBars
            byDecade={data.aggregates.byDecade}
            username={
              data.letterboxdUsername ??
              profile.letterboxd_username ??
              undefined
            }
          />
        </div>
      </Field>

      <Tear />

      <Field>
        <Kicker>Your logging habit</Kicker>
        <H3>Logged per month</H3>
        <div className="mt-4">
          <HeatStrip loggedByMonth={data.aggregates.loggedByMonth} />
        </div>
      </Field>

      <Tear />

      {/* Blends */}
      <Field>
        <Kicker>People you've blended with</Kicker>
        <H3>Your blends</H3>
        {blends.length === 0 ? (
          <Empty>
            Send your link to someone whose taste you're curious about — your
            blends will print here.
          </Empty>
        ) : (
          <ul className="mt-3">
            {blends.map((b) => (
              <li key={b.blend_slug}>
                <Link
                  to={`/b/${b.blend_slug}`}
                  data-testid="blend-row"
                  className="flex items-center justify-between border-b border-divider py-3"
                >
                  <span className="font-semibold text-ink-900">
                    {b.partner_name}
                  </span>
                  <span className="font-mono text-xs uppercase tracking-stamp text-ink-400">
                    {b.films_in_common} film{b.films_in_common === 1 ? "" : "s"}{" "}
                    in common →
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Field>

      {/* Footer — barcode + account actions */}
      <div className="flex flex-col gap-4 px-6 py-5 sm:px-10">
        <div className="flex items-center gap-6">
          <p className="mono-meta flex-1 leading-relaxed text-ink-400">
            Export from {data.exportedAt} ·{" "}
            <button
              className="underline"
              data-testid="action-update-toggle"
              onClick={() => setShowUpdate((v) => !v)}
            >
              Update
            </button>
          </p>
          <Barcode className="hidden w-[200px] shrink-0 sm:block" />
        </div>
        {showUpdate && (
          <div className="max-w-md">
            <Dropzone
              compact
              onParsed={async (p) => {
                await updateProfile(p);
                const fresh = await getMyProfile();
                if (fresh) onUpdated(fresh);
                setShowUpdate(() => false);
              }}
            />
          </div>
        )}
        <div className="flex gap-4 text-sm">
          <button
            className="text-ink-400 underline"
            data-testid="action-signout"
            onClick={onSignOut}
          >
            Sign out
          </button>
          <button
            className="text-red-600 underline"
            data-testid="action-delete"
            onClick={onDelete}
          >
            Delete account
          </button>
        </div>
      </div>
    </Sheet>
  );
}

function Gauge({
  value,
  label,
  sub,
  tone,
}: {
  value: string;
  label: string;
  sub: string;
  tone: "red" | "gold" | "clay" | "ink";
}) {
  const color =
    tone === "red"
      ? "text-red-600"
      : tone === "gold"
        ? "text-gold-600"
        : tone === "clay"
          ? "text-clay-500"
          : "text-ink-900";
  return (
    <div>
      <div
        className={`font-display text-2xl font-extrabold leading-tight tracking-tightish ${color}`}
      >
        {value}
      </div>
      <div className="mt-1 font-mono text-[10px] uppercase tracking-stamp text-ink-500">
        {label}
      </div>
      <div className="text-[11px] text-ink-400">{sub}</div>
    </div>
  );
}

/**
 * Your public taste profile: a share button (the pretty path), the URL, and an
 * auto-saving "hide" checkbox. Hiding makes /:username 404 for everyone else;
 * invite links and blends keep working.
 */
function PublicPageRow({
  username,
  initialHidden,
  initialSyncDisabled,
  syncedCount,
}: {
  username: string;
  initialHidden: boolean;
  initialSyncDisabled: boolean;
  syncedCount: number;
}) {
  const [hidden, setHidden] = useState(initialHidden);
  const [syncOff, setSyncOff] = useState(initialSyncDisabled);
  const [saved, setSaved] = useState(false);
  const [shared, setShared] = useState(false);
  const url = `${typeof window !== "undefined" ? window.location.origin : "https://twoshot.xyz"}/${username}`;

  async function toggle(next: boolean) {
    setHidden(next); // optimistic — flip back on failure
    try {
      await setPublicHidden(next);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setHidden(!next);
    }
  }

  async function toggleSync(next: boolean) {
    setSyncOff(next); // optimistic — flip back on failure
    try {
      await setSyncDisabled(next);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setSyncOff(!next);
    }
  }

  async function share() {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: `${username}'s taste profile on twoshot`,
          url,
        });
        return;
      } catch {
        /* fall through to copy */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setShared(true);
      setTimeout(() => setShared(false), 2000);
    } catch {
      /* ignore */
    }
  }

  return (
    <div
      className="mt-4 border-t border-divider pt-4"
      data-testid="public-page-row"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <button
          type="button"
          data-testid="action-share-profile"
          onClick={share}
          disabled={hidden}
          className="inline-flex min-h-[38px] items-center gap-2 rounded-pill border-2 border-ink-900 bg-paper-50 px-4 text-sm font-semibold text-ink-900 transition-colors hover:bg-gold-500 disabled:opacity-40"
        >
          {shared ? "Link copied!" : "Share your taste profile ✨"}
        </button>
        <span
          className={`mono-meta ${hidden ? "text-ink-300 line-through" : "text-ink-400"}`}
          data-testid="public-page-link"
        >
          {hidden ? (
            <>twoshot.xyz/{username}</>
          ) : (
            <Link
              to={`/${username}`}
              className="font-bold text-ink-700 underline hover:text-red-600"
            >
              twoshot.xyz/{username}
            </Link>
          )}
        </span>
      </div>
      <label className="mt-2.5 flex cursor-pointer items-center gap-2 text-sm text-ink-500">
        <input
          type="checkbox"
          data-testid="toggle-public-hidden"
          checked={hidden}
          onChange={(e) => toggle(e.target.checked)}
          className="h-4 w-4 accent-red-600"
        />
        Hide my public page
        <span
          aria-live="polite"
          className={`mono-meta text-green-700 transition-opacity duration-300 ${saved ? "opacity-100" : "opacity-0"}`}
        >
          Saved ✓
        </span>
      </label>
      <label className="mt-1.5 flex cursor-pointer items-center gap-2 text-sm text-ink-500">
        <input
          type="checkbox"
          data-testid="toggle-sync-disabled"
          checked={!syncOff}
          onChange={(e) => toggleSync(!e.target.checked)}
          className="h-4 w-4 accent-red-600"
        />
        Keep this up to date from my public Letterboxd diary
      </label>
      <p className="mono-meta mt-1 normal-case tracking-normal text-ink-400">
        {syncOff
          ? "Paused — your page shows your last upload only."
          : syncedCount > 0
            ? `${syncedCount.toLocaleString()} film${syncedCount === 1 ? "" : "s"} added since your export.`
            : "We top up new logs from your public diary; re-upload any time to re-baseline."}
      </p>
    </div>
  );
}

function InviteRow({ shareSlug }: { shareSlug: string }) {
  const [copied, setCopied] = useState(false);
  const url = `${typeof window !== "undefined" ? window.location.origin : "https://twoshot.xyz"}/with/${shareSlug}`;
  async function share() {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: "Blend film taste on twoshot", url });
        return;
      } catch {
        /* fall through */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  }
  return (
    <div className="mt-3 flex flex-col gap-2 sm:flex-row">
      <input
        readOnly
        data-testid="invite-url"
        value={url}
        className="min-w-0 flex-1 rounded-pill border border-border-strong bg-paper-50 px-4 py-2.5 text-sm text-ink-900"
        onFocus={(e) => e.currentTarget.select()}
      />
      <button
        type="button"
        data-testid="action-copy-invite"
        onClick={share}
        className="inline-flex min-h-[42px] items-center justify-center rounded-pill bg-gold-500 px-5 text-sm font-semibold text-ink-900 transition-colors hover:bg-gold-400"
      >
        {copied ? "Copied!" : "Copy / Share"}
      </button>
    </div>
  );
}
